import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  title,
  size = 'default',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = ''
}) => {
  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-md',
    default: 'max-w-2xl',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl',
    full: 'max-w-7xl'
  };

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  const stopScrollPropagation = (e) => {
    e.stopPropagation();
  };
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm"
      onClick={handleBackdropClick}
      onWheel={stopScrollPropagation}
      onTouchMove={stopScrollPropagation}
    >
      <div className={cn(
        'bg-white rounded-2xl w-full max-h-[95vh] overflow-hidden shadow-2xl',
        sizeClasses[size],
        className
      )}>
        {(title || showCloseButton) && (
          <ModalHeader title={title} onClose={showCloseButton ? onClose : null} />
        )}
        <div
          className="overflow-y-auto max-h-[calc(95vh-100px)]"
          onWheel={stopScrollPropagation}
          onTouchMove={stopScrollPropagation}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const ModalHeader = ({ title, onClose, children, className = '' }) => {
  return (
    <div className={cn(
      'relative bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200',
      className
    )}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {title && (
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          )}
          {children}
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-full ml-4"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
};

const ModalContent = ({ children, className = '' }) => {
  return (
    <div className={cn('p-4 lg:p-6', className)}>
      {children}
    </div>
  );
};

const ModalFooter = ({ children, className = '' }) => {
  return (
    <div className={cn(
      'px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3',
      className
    )}>
      {children}
    </div>
  );
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;

export default Modal;
